/**
 * Scrolls a column of `table` into view, clear of the sticky columns, and tints its header and
 * rendered cells so the eye finds it. With reduced motion it jumps there untinted.
 */
export const revealColumn = (table: HTMLTableElement, columnId: string) => {
  const header = table.querySelector(`th[data-column-id="${CSS.escape(columnId)}"]`)
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

  header?.scrollIntoView({
    behavior: reduceMotion ? 'instant' : 'smooth',
    block: 'nearest',
    inline: 'nearest',
  })
  if (reduceMotion) return

  // A long table takes a while to scroll, so the tint waits until the header has held still for
  // a few frames, then holds a second before fading to each cell's own background. The header's
  // position settles whichever ancestor scrolls, so this doesn't depend on the table's markup.
  let last = header?.getBoundingClientRect().left
  let stillFrames = 0
  const tintOnceSettled = () => {
    const current = header?.getBoundingClientRect().left
    stillFrames = current === last ? stillFrames + 1 : 0
    last = current
    if (stillFrames < 3) {
      requestAnimationFrame(tintOnceSettled)
      return
    }

    // Found once the scroll is done: a table that renders only the columns in view renders this
    // one's body cells as it scrolls there.
    const cells = table.querySelectorAll<HTMLElement>(
      `:is(th, td)[data-column-id="${CSS.escape(columnId)}"]`,
    )
    const tint = { backgroundColor: 'var(--color-table-selected)' }
    for (const cell of cells) {
      cell.animate(
        [
          { ...tint, offset: 0 },
          { ...tint, easing: 'cubic-bezier(0.23, 1, 0.32, 1)', offset: 0.5 },
        ],
        { duration: 2000 },
      )
    }
  }
  requestAnimationFrame(tintOnceSettled)
}
